import { config, isChatStatusBusy, useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

import { HideCanvasAttachmentsContext } from '@/components/blocks/Message/attachments-context';
import { YStack } from '@/components/ui';
import { chatDebugStore } from '@/features/chat-debug';
import { chatStore } from '@/features/chat/chat.store';
import { SuggestedProjectBanner } from '@/features/chat/SuggestedProjectBanner';
import { TodoPlanPanel } from '@/features/chat/todo-plan-panel/TodoPlanPanel';
import { useThreadVisibility } from '@/features/chat/use-thread-visibility';
import { useMarkFeedChatSeenWhileVisible } from '@/features/feed/use-mark-feed-chat-seen';
import { useResizeObserver } from '@/hooks/useResizeObserver';
import { cn } from '@/lib/utils';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';
import { trackEvent } from '@/utils/analytics';
import { logChatRenderDebug } from '@/utils/chat-render-debug';
import { classList } from '@/utils/cn';
import { COMPOSER_MIN_HEIGHT, NEW_CHAT_ID } from '@/utils/constants';
import { oncePerArgs } from '@/utils/jsUtils';

import { FileDropZone } from '../FileDropZone';
import MessageList from '../Message/MessageList/MessageList';

import { Composer, type ComposerCollapsed } from './Composer';

const trackMessageInputStartedEvent = oncePerArgs(
  (chatId: string, _chatLength: number) =>
    trackEvent('MESSAGE_INPUT_STARTED', {
      chat_id: chatId,
    })
);

const MessageInputStartedTracker = observer(
  function MessageInputStartedTracker({ threadId }: { threadId: string }) {
    const { drafts: draftsStore, messages: messagesStore } = useStores();
    const draftHasContent = Boolean(
      draftsStore.getDraft(threadId)?.content.length
    );
    const messageCount = messagesStore.getMessagesByThreadId(threadId).length;

    useEffect(() => {
      if (!draftHasContent) return;
      trackMessageInputStartedEvent(threadId, messageCount);
    }, [draftHasContent, messageCount, threadId]);

    return null;
  }
);

type ThreadProps = {
  threadId: string;
  showChatBanners?: boolean;
  composerInitiallyCollapsed?: ComposerCollapsed;
  composerPlaceholder?: string;
  content?: ReactNode;
  contentHeader?: ReactNode;
  hideCanvasAttachments?: boolean;
  hideFirstTurnActivity?: boolean;
  wrapFirstMessage?: (message: ReactNode) => ReactNode;
  /** Rendered once, under the last message of the thread's first response */
  afterFirstResponse?: ReactNode;
  onComposerExpanded?: () => void;
  composerVisible?: boolean;
};

export const Thread = observer(
  ({
    threadId,
    showChatBanners = true,
    composerInitiallyCollapsed,
    composerPlaceholder,
    content,
    contentHeader,
    hideCanvasAttachments = false,
    hideFirstTurnActivity = false,
    wrapFirstMessage,
    afterFirstResponse,
    onComposerExpanded,
    composerVisible,
  }: ThreadProps) => {
    const { threads: threadsStore } = useStores();
    const thread = threadsStore.getById(threadId);
    const isBusy = isChatStatusBusy(thread?.status);
    logChatRenderDebug(
      'thread-render',
      { threadStatus: thread?.status },
      threadId
    );
    const previousBusyStateRef = useRef({ threadId, isBusy });
    const [expandedComposerThreadId, setExpandedComposerThreadId] = useState<
      string | null
    >(null);
    const composerCollapsed =
      Boolean(composerInitiallyCollapsed) &&
      (content !== undefined || expandedComposerThreadId !== threadId);
    const composerFloats =
      composerCollapsed && content !== undefined && content !== null;
    const expandComposer = (target: EventTarget) => {
      const element = target instanceof HTMLElement ? target : null;
      if (!element?.closest('textarea, [contenteditable="true"]')) return;
      setExpandedComposerThreadId(threadId);
      onComposerExpanded?.();
    };
    const isInternalUser = userStore.isInternalUser;
    const isDraggingFile = uiStore.isDraggingFile;
    const chatWidth = uiStore.chatWidth;
    const [threadContainerRef, threadRect] =
      useResizeObserver<HTMLDivElement>();
    const [messageBoxRef, messageBoxRect] = useResizeObserver<HTMLDivElement>();
    const [contentHeaderRef, contentHeaderRect] =
      useResizeObserver<HTMLDivElement>();
    const messageListHeight = useMemo(
      () =>
        Math.max(
          threadRect.height - messageBoxRect.height - contentHeaderRect.height,
          COMPOSER_MIN_HEIGHT
        ),
      [contentHeaderRect.height, messageBoxRect.height, threadRect.height]
    );
    const sendAskUserAnswersRef = useRef<() => void>(() => {});
    const sendAskUserAnswers = useCallback(
      () => sendAskUserAnswersRef.current(),
      []
    );

    useThreadVisibility(threadId);
    useMarkFeedChatSeenWhileVisible(
      threadId,
      chatStore.getThreadBackgroundStatus(threadId) === undefined
    );

    useEffect(() => {
      if (!composerCollapsed) return;
      const activeElement = document.activeElement;
      if (
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement instanceof HTMLElement &&
          activeElement.isContentEditable)
      ) {
        activeElement.blur();
      }
    }, [composerCollapsed]);

    useEffect(() => {
      if (threadId === NEW_CHAT_ID) return;
      void threadsStore.openThread(threadId);
    }, [threadId, threadsStore]);

    useEffect(() => {
      const previous = previousBusyStateRef.current;
      if (previous.threadId === threadId && previous.isBusy && !isBusy) {
        trackEvent('MESSAGE_COMPLETED');
      }
      previousBusyStateRef.current = { threadId, isBusy };
    }, [isBusy, threadId]);

    useEffect(() => {
      if (!isInternalUser || threadId === NEW_CHAT_ID) return;
      chatDebugStore.setActiveChatId(threadId);
      return () => chatDebugStore.clearActiveChatId(threadId);
    }, [isInternalUser, threadId]);

    const setIsDraggingFile = useCallback(
      (value: boolean) => uiStore.setIsDraggingFile(value),
      []
    );

    return (
      <>
        <MessageInputStartedTracker threadId={threadId} />
        <FileDropZone show={isDraggingFile} />
        <YStack
          className={cn('relative min-h-0 min-w-0 flex-1 select-text', {
            'opacity-10': isDraggingFile,
          })}
          ref={threadContainerRef}
        >
          {contentHeader && (
            <div ref={contentHeaderRef} className="shrink-0">
              {contentHeader}
            </div>
          )}
          <YStack className="min-h-0 flex-1 overflow-hidden">
            {content ?? (
              <HideCanvasAttachmentsContext.Provider
                value={hideCanvasAttachments}
              >
                <MessageList
                  threadId={threadId}
                  height={messageListHeight}
                  previousTurnPreview={config.chat.previous_turn_preview}
                  hideFirstTurnActivity={hideFirstTurnActivity}
                  wrapFirstMessage={wrapFirstMessage}
                  afterFirstResponse={afterFirstResponse}
                  onAskUserComplete={sendAskUserAnswers}
                />
              </HideCanvasAttachmentsContext.Provider>
            )}
          </YStack>
          <YStack
            ref={messageBoxRef}
            aria-hidden={composerVisible === false ? true : undefined}
            inert={composerVisible === false ? true : undefined}
            className={cn(
              classList(
                'w-full self-center',
                'text-foreground',
                'pb-2 md:pb-4 px-3 md:px-12',
                chatWidth
              ),
              composerFloats && 'absolute inset-x-0 bottom-0 z-10 mx-auto',
              composerVisible === false && 'pointer-events-none opacity-0'
            )}
            style={{
              minHeight: composerCollapsed
                ? undefined
                : `${COMPOSER_MIN_HEIGHT}px`,
            }}
            onPointerDownCapture={
              composerCollapsed
                ? (event) => expandComposer(event.target)
                : undefined
            }
            onFocusCapture={
              composerCollapsed
                ? (event) => expandComposer(event.target)
                : undefined
            }
          >
            {showChatBanners && (
              <>
                <TodoPlanPanel threadId={threadId} />
                <SuggestedProjectBanner threadId={threadId} />
              </>
            )}
            <Composer
              focusOnType
              threadId={threadId}
              placeholder={composerPlaceholder}
              setIsDraggingFile={setIsDraggingFile}
              collapsed={
                composerInitiallyCollapsed === undefined
                  ? undefined
                  : composerCollapsed
                    ? composerInitiallyCollapsed
                    : false
              }
              sendAskUserAnswersRef={
                content === undefined || content === null
                  ? sendAskUserAnswersRef
                  : undefined
              }
            />
          </YStack>
        </YStack>
      </>
    );
  }
);
